// PostHog event names + prop shapes for feature-flag RESOLUTION (RET-1922).
// Per-domain constants pattern, same as alertCenterAnalytics.constants.
//
// These instrument the resolution itself, not any feature behind a flag. The
// bug they exist to measure: when the first flags fetch of a page load fails
// (dead network, or a 200 whose body is not a flags map), the provider falls
// back to DEFAULT_FLAGS and the D11 snapshot freezes that OFF for the whole
// session — the fail-safe is correct, but it was completely invisible, so a
// ~1-in-3 failure rate in flag-on workspaces only surfaced as legacy events
// arriving from workspaces that should have been on the new experience.
//
// Transport note (deliberate divergence from every other constants file
// here): these are emitted with `getAnalytics().track` DIRECTLY, not through
// `useAnalytics().trackEvent`. `performFetch` is a `useCallback` with `[]`
// deps that already carries the workspace id it fetched for, and the hook
// early-returns when there is no workspace in context — exactly the state a
// failing first resolution can be in. Hence `workspace_id` is an explicit
// prop below instead of an injected one.
import type { tFeatureFlagsFailureCause, tFeatureFlagsFailureReason } from 'src/services/FeatureFlags/FeatureFlags.functions';
import type { tBootProperties } from './sessionAnalytics.constants';

export const FEATURE_FLAGS_EVENTS = {
  /**
   * Every retry of a resolution failed; the session falls back to defaults.
   * This is THE rate counter for the bug: in a healthy deployment it should
   * be a rounding error, and its `reason` split says whether the cause is
   * transport (`network`) or the backend answering something that is not
   * flags (`invalid_envelope` / `backend_error`).
   */
  RESOLUTION_FAILED: 'front.flags.resolution_failed',
  /**
   * An attempt after the first one succeeded. Emitted only on recovery, so
   * `recovered / (recovered + failed)` measures how much the retry actually
   * saves — i.e. whether the backoff is worth its 1.3s worst case.
   */
  RESOLUTION_RECOVERED: 'front.flags.resolution_recovered',
} as const;

/**
 * `first-load` = the resolution that decides the session (no successful fetch
 * for this workspace yet, so the D11 snapshot freezes on its outcome).
 * `refetch` = a later socket-driven or imperative resolution, which cannot
 * degrade the snapshot and is therefore far less severe.
 */
export type tFlagResolutionPhase = 'first-load' | 'refetch';

export type tFlagResolutionFailedProps = tBootProperties & {
  workspace_id: string;
  reason: tFeatureFlagsFailureReason;
  /** Attempts made before giving up (initial + retries). */
  attempts: number;
  phase: tFlagResolutionPhase;
  last_failure_cause: tFeatureFlagsFailureCause;
  http_status?: number;
};

export type tFlagResolutionRecoveredProps = tBootProperties & {
  workspace_id: string;
  /** Which attempt succeeded (always >= 2 — attempt 1 is not a recovery). */
  attempts: number;
  first_failure_cause: tFeatureFlagsFailureCause;
};
